/**
 * MicrosoftCallbackPage
 *
 * Landing page for the server-side Microsoft OAuth2 PKCE redirect.
 * Microsoft redirects the user here after login with ?code=...&state=...
 * This page sends those values to our backend, then redirects the user
 * back to /integrations on success.
 *
 * This page is accessible even before the user is fully authenticated
 * in the app (the JWT cookie is still present from the previous session
 * so the backend callback call will succeed).
 */
import { useEffect, useRef, useState } from "react";
import { CheckCircle, XCircle, ArrowsClockwise } from "@phosphor-icons/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RETURN_PATH = "/integrations";

export default function MicrosoftCallbackPage() {
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params   = new URLSearchParams(window.location.search);
    const code     = params.get("code");
    const state    = params.get("state");
    const errParam = params.get("error");

    if (errParam) {
      setStatus("error");
      setMessage(params.get("error_description") || "Microsoft authorization was denied.");
      return;
    }

    if (!code || !state) {
      setStatus("error");
      setMessage("Missing authorization code or state. Please try again.");
      return;
    }

    (async () => {
      try {
        await axios.post(
          `${API}/auth/microsoft/callback`,
          { code, state },
          { withCredentials: true },
        );
        setStatus("success");
        setMessage("Microsoft account connected successfully!");
        // Full reload so the auth-protected layout and integrations page mount fresh
        setTimeout(() => { window.location.href = RETURN_PATH; }, 2000);
      } catch (err) {
        const detail = err.response?.data?.detail || err.message || "Something went wrong.";
        setStatus("error");
        setMessage(detail);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="bg-white border border-slate-200 rounded-lg p-10 max-w-md w-full text-center shadow-sm">
        {status === "loading" && (
          <>
            <ArrowsClockwise size={36} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-slate-600 text-sm font-medium">Connecting Microsoft account…</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle size={40} className="text-emerald-500 mx-auto mb-4" weight="fill" />
            <p className="text-slate-800 font-semibold mb-1">Connected!</p>
            <p className="text-slate-500 text-sm">{message}</p>
            <p className="text-xs text-slate-400 mt-3">Redirecting you back…</p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle size={40} className="text-red-500 mx-auto mb-4" weight="fill" />
            <p className="text-slate-800 font-semibold mb-2">Connection Failed</p>
            <p className="text-slate-500 text-sm mb-4">{message}</p>
            {message.toLowerCase().includes("token exchange") && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 text-left">
                <p className="text-xs font-semibold text-amber-800 mb-1">Azure AD setup required</p>
                <p className="text-xs text-amber-700 mb-2">
                  Add the following redirect URI to your Azure AD app registration
                  under <strong>Authentication → Redirect URIs</strong>:
                </p>
                <code className="block text-[11px] bg-white border border-amber-200 rounded px-2 py-1 break-all text-amber-900">
                  {window.location.origin}/microsoft-callback
                </code>
              </div>
            )}
            {/* Full page load ensures the integrations page layout mounts correctly */}
            <button
              onClick={() => { window.location.href = RETURN_PATH; }}
              className="text-sm font-medium text-primary hover:underline"
            >
              Back to Integrations
            </button>
          </>
        )}
      </div>
    </div>
  );
}
